<template>
  <div v-bind="$attrs">
    <InputRow
      v-slot="{ field: inputField }"
      v-model="comment"
      name="comment"
      :display-name="t('components.TaskModal.comment')"
      required
    >
      <LISAInput
        ref="commentFocus"
        v-bind="inputField"
        type="textarea"
        rows="1"
        @keydown.ctrl.enter="addComment"
        @keydown.meta.enter="addComment"
        @keydown.esc="$emit('commentAdded')"
        @paste.stop
      >
        <template v-if="isAnonym" #prepend>
          <InputRow
            v-slot="{ field: inputField2, validation: { valid: valid2 } }"
            v-model="commentUserIdRef"
            name="commentUserId"
            :display-name="t('components.TaskModal.commentUser')"
            required
          >
            <SearchSelect
              v-bind="inputField2"
              :model-value="commentUserIdRef ?? undefined"
              :mode="SelectMode.USER"
              :state="valid2"
              :filters="userFilters"
              @update:model-value="handleInput"
            />
          </InputRow>
        </template>
        <template #append>
          <div class="bg-white">
            <LISAButton
              :full-height="true"
              variant="success"
              text
              :full-width="true"
              :disabled="validation.invalid"
              @click="addComment"
            >
              {{ t("components.event-action.TaskCreateComment.send") }}
            </LISAButton>
          </div>
        </template>
      </LISAInput>
    </InputRow>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import InputRow from "@/components/shared/form/InputRow.vue";
import { ref, computed, onMounted } from "vue";
import LISAButton from "@LISAPrime/LISAButton.vue";
import { LISAInput } from "@fi-solutions/lisa-ui";
import { useSQDCActionCommentCreateMutation, useSQDCEventCommentCreateMutation } from "@/graphql";
import SearchSelect from "@/components/shared/searchSelects/SearchSelect.vue";
import { SelectMode } from "@/components/shared/searchSelects/SelectDefinitions";
import store from "@/store";
import { useFormValidation } from "@/modules/formValidation";
import {
  useProvidedBoardContext,
  useSpecifiedBoardContext,
} from "@/modules/sqdc/helper/EABConfigHelper";

defineOptions({ inheritAttrs: false });

const props = defineProps({
  eventId: {
    type: Number,
    required: false,
    default: undefined,
  },
  actionId: {
    type: Number,
    required: false,
    default: undefined,
  },
  originalBoardId: {
    type: Number,
    required: false,
    default: undefined,
  },
});

const {
  mutate: mutateAction,
  loading: loadingAction,
  onDone: onDoneAction,
} = useSQDCActionCommentCreateMutation(() => ({
  variables: {
    actionId: props.actionId!,
    comment: comment.value,
    commentUserId: commentUserIdRef.value as number,
  },
}));
const {
  mutate: mutateEvent,
  loading: loadingEvent,
  onDone: onDoneEvent,
} = useSQDCEventCommentCreateMutation(() => ({
  variables: {
    eventId: props.eventId!,
    comment: comment.value,
    commentUserId: commentUserIdRef.value as number,
  },
}));
const emit = defineEmits<{
  commentAdded: [];
}>();

onDoneAction(() => {
  emit("commentAdded");
});

onDoneEvent(() => {
  emit("commentAdded");
});

const loading = computed(() => loadingAction.value || loadingEvent.value);

const comment = ref("");
const commentFocus = ref<InstanceType<typeof LISAInput>>();
onMounted(() => {
  if (commentFocus.value != null && "$el" in commentFocus.value) {
    const el = commentFocus.value.$el as HTMLElement;
    el.querySelector("textarea")?.focus();
  }
});
const isAnonym = ref<boolean>(store.getters["login/isAnonym"]);
const userId = ref<number | null>(isAnonym.value ? null : Number(store.getters["login/userId"]));

const commentUserId = computed<number | null>({
  get() {
    return store.getters["taskComment/getTaskCommentatorId"];
  },
  set(value: number | null) {
    userId.value = value;
  },
});
const commentUserIdRef = ref(commentUserId.value);

async function handleInput(id: number | null | undefined) {
  if (id == null || isAnonym.value === false) {
    return;
  }
  await store.dispatch("taskComment/setTaskCommentatorId", id);
}
const validation = useFormValidation({ name: "TaskCreateComponent" });

async function addComment() {
  if (!loading.value && (await validation.validate())) {
    if (props.eventId != null) {
      await mutateEvent();
    } else if (props.actionId != null) {
      await mutateAction();
    }
  }
}

const { siteId } = useProvidedBoardContext();
const { siteId: originalSiteId } = useSpecifiedBoardContext(computed(() => props.originalBoardId));

const userFilters = computed(() => {
  const siteIdLocal = siteId.value ?? originalSiteId.value;
  if (siteIdLocal == null) {
    return undefined;
  }
  return { availableOnSiteIds: [siteIdLocal] };
});

const { t } = useTranslation();
</script>
